import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import { usePostNpsTriggerMutation } from "@/api/nps/useNpsAPI";
import { useGetUserProfileAPI } from "@/api/userProfile/useUserProfileAPI";
import { NPS_COOLDOWN_MS, NPS_TRIGGER_DELAY_MS } from "@/constants/nps";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import { RootState } from "@/store";
import {
  setNpsId,
  setNpsMilestoneReached,
  setShowNpsModal
} from "@/store/slice/uiSlice";

export const useNpsTrigger = () => {
  const dispatch = useDispatch();
  const { isNpsModalEnabled } = useAppFeatures();
  const { npsMilestoneReached, showNpsModal } = useSelector(
    (state: RootState) => state.ui
  );
  const { hasLoggedIn } = useSelector((state: RootState) => state.auth);
  const delayTimeoutRef = useRef<NodeJS.Timeout>();

  const { data: userProfile } = useGetUserProfileAPI({
    enabled: hasLoggedIn && isNpsModalEnabled
  });

  const { mutate: triggerNps } = usePostNpsTriggerMutation({
    onSuccess: (data) => {
      if (data.show) {
        dispatch(setNpsId(data.id));

        if (userProfile?.email_address) {
          const cacheKey = `nps_last_shown_${userProfile.email_address}`;
          localStorage.setItem(cacheKey, new Date().toISOString());
        }

        // Wait 3 seconds before rendering the modal (per spec)
        delayTimeoutRef.current = setTimeout(() => {
          dispatch(setShowNpsModal(true));
        }, NPS_TRIGGER_DELAY_MS);
      }
      dispatch(setNpsMilestoneReached(null));
    },
    onError: () => {
      dispatch(setNpsMilestoneReached(null));
    }
  });

  useEffect(() => {
    if (!isNpsModalEnabled) return;

    if (npsMilestoneReached && !showNpsModal && userProfile?.email_address) {
      const emailAddress = userProfile.email_address;
      const cacheKey = `nps_last_shown_${emailAddress}`;
      const lastShown = localStorage.getItem(cacheKey);

      const isWithinCooldown =
        lastShown &&
        Date.now() - new Date(lastShown).getTime() < NPS_COOLDOWN_MS;

      if (isWithinCooldown) {
        dispatch(setNpsMilestoneReached(null));
        return;
      }

      // Call the trigger API immediately; the 3s delay happens after success
      triggerNps({ milestone_trigger: npsMilestoneReached });
    }
  }, [
    npsMilestoneReached,
    showNpsModal,
    userProfile,
    dispatch,
    isNpsModalEnabled,
    triggerNps
  ]);

  // Clean up the delay timeout on unmount only
  useEffect(() => {
    return () => {
      if (delayTimeoutRef.current) clearTimeout(delayTimeoutRef.current);
    };
  }, []);
};
